import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSession } from './SessionProvider.jsx';
import {
  decideEpisodeAccess,
  decideTitleAccess,
  fetchViewerEntitlements,
  normalizeSubscription,
  normalizeTvod,
} from '../../services/access/entitlementService.js';

const guestSubscription = normalizeSubscription({ status: 'GUEST', svod_active: false, has_access: false });
const guestTvod = normalizeTvod({ items: [], title_ids: [] });

const AccessContext = createContext({
  ready: false,
  loading: true,
  error: null,
  subscription: guestSubscription,
  tvod: guestTvod,
  refresh: async () => {},
  titleAccess: () => ({ allowed: true, adMode: 'none', reason: 'open' }),
  episodeAccess: () => ({ allowed: true, adMode: 'none', reason: 'open' }),
});

export function AccessProvider({ children }) {
  const { ready: sessionReady, session, user } = useSession();
  const [state, setState] = useState({
    ready: false,
    loading: true,
    error: null,
    subscription: guestSubscription,
    tvod: guestTvod,
  });

  const refresh = useCallback(async () => {
    if (!sessionReady) return null;
    if (!session?.user) {
      const guest = { ready: true, loading: false, error: null, subscription: guestSubscription, tvod: guestTvod };
      setState(guest);
      return guest;
    }

    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const entitlements = await fetchViewerEntitlements(session);
      const next = { ready: true, loading: false, error: null, ...entitlements };
      setState(next);
      return next;
    } catch (error) {
      // Paid access fails closed. Never promote metadata/localStorage into entitlement state.
      const next = {
        ready: true,
        loading: false,
        error,
        subscription: guestSubscription,
        tvod: guestTvod,
      };
      setState(next);
      return next;
    }
  }, [sessionReady, session?.access_token, session?.user?.id]);

  useEffect(() => {
    let active = true;
    if (!sessionReady) return undefined;

    if (!session?.user) {
      setState({ ready: true, loading: false, error: null, subscription: guestSubscription, tvod: guestTvod });
      return undefined;
    }

    setState((current) => ({ ...current, loading: true, error: null }));
    fetchViewerEntitlements(session)
      .then((entitlements) => {
        if (!active) return;
        setState({ ready: true, loading: false, error: null, ...entitlements });
      })
      .catch((error) => {
        if (!active) return;
        setState({ ready: true, loading: false, error, subscription: guestSubscription, tvod: guestTvod });
      });

    return () => { active = false; };
  }, [sessionReady, session?.access_token, session?.user?.id]);

  const titleAccess = useCallback((item, kind = 'content') => decideTitleAccess({
    item,
    kind,
    user,
    subscription: state.subscription,
    tvod: state.tvod,
  }), [user?.id, state.subscription, state.tvod]);

  const episodeAccess = useCallback((series, episode, kind = 'content') => decideEpisodeAccess({
    series,
    episode,
    kind,
    user,
    subscription: state.subscription,
    tvod: state.tvod,
  }), [user?.id, state.subscription, state.tvod]);

  const value = useMemo(() => ({
    ...state,
    refresh,
    titleAccess,
    episodeAccess,
  }), [state, refresh, titleAccess, episodeAccess]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess() {
  return useContext(AccessContext);
}
